import {LitElement, html, nothing} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';

import {wsTextFieldStyles} from './ws-text-field.styles.js';

export type WsTextFieldType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'url'
  | 'tel'
  | 'search'
  | 'textarea';
export type WsTextFieldSize = 'small' | 'medium' | 'large';
export type WsTextFieldShape = 'default' | 'circle';

type WsTextFieldControl = HTMLInputElement | HTMLTextAreaElement;

let nextTextFieldId = 0;

/**
 * Workshop form-associated text field.
 *
 * Use `type="textarea"` for multiline values. Search fields use the circular
 * shape unless `shape="default"` is set.
 *
 * @fires input - Dispatched when the value changes while editing.
 * @fires change - Dispatched when the edited value is committed.
 * @slot leading-icon - Optional icon rendered before the input.
 * @slot trailing-icon - Optional icon rendered after the input.
 * @csspart label - The visible field label.
 * @csspart control - The field container.
 * @csspart input - The native input or textarea element.
 * @csspart supporting-text - Helper or error text.
 * @csspart clear-button - The optional clear action.
 */
@customElement('ws-text-field')
export class WsTextField extends LitElement {
  static override styles = wsTextFieldStyles;
  static formAssociated = true;

  /** Input value submitted with the enclosing form. */
  @property()
  value = '';

  /** Name used when the field participates in form submission. */
  @property({reflect: true})
  name = '';

  /** Native control type. Use `textarea` for multiline text. */
  @property({reflect: true})
  type: WsTextFieldType = 'text';

  /** Number of visible text rows when `type="textarea"`. */
  @property({type: Number, reflect: true})
  rows = 3;

  /** Control height and density. */
  @property({reflect: true})
  size: WsTextFieldSize = 'medium';

  /**
   * Shape override. Without an override, search fields are circular and all
   * other field types use the default shape.
   */
  @property({reflect: true})
  shape?: WsTextFieldShape;

  /** Visible label displayed above the control. */
  @property()
  label = '';

  /** Placeholder forwarded to the native control. */
  @property()
  placeholder = '';

  /** Supporting guidance shown while the field is valid. */
  @property({attribute: 'helper-text'})
  helperText = '';

  /** Supporting message shown while the field is invalid. */
  @property({attribute: 'error-text'})
  errorText = '';

  /** Whether a non-empty value is required. */
  @property({type: Boolean, reflect: true})
  required = false;

  /** Disables interaction and excludes the value from form submission. */
  @property({type: Boolean, reflect: true})
  disabled = false;

  /** Prevents editing while preserving focus and form submission. */
  @property({type: Boolean, reflect: true, attribute: 'readonly'})
  readOnly = false;

  /** Applies an externally controlled invalid state. */
  @property({type: Boolean, reflect: true})
  invalid = false;

  /** Shows a clear action while the field contains a value. */
  @property({type: Boolean, reflect: true})
  clearable = false;

  /** Browser autofill hint. */
  @property()
  autocomplete: HTMLInputElement['autocomplete'] = '';

  /** Virtual keyboard hint. */
  @property({attribute: 'inputmode'})
  override inputMode = '';

  /** Minimum accepted text length. */
  @property({type: Number, attribute: 'minlength'})
  minLength?: number;

  /** Maximum accepted text length. */
  @property({type: Number, attribute: 'maxlength'})
  maxLength?: number;

  /** Minimum numeric value. */
  @property()
  min?: string;

  /** Maximum numeric value. */
  @property()
  max?: string;

  /** Numeric increment. */
  @property()
  step?: string;

  /** Validation pattern forwarded to the native input. */
  @property()
  pattern?: string;

  /** Accessible name used when no visible label is provided. */
  @property({attribute: 'aria-label'})
  accessibleLabel?: string;

  @state()
  private hasLeadingIcon = false;

  @state()
  private hasTrailingIcon = false;

  @state()
  private touched = false;

  @state()
  private formDisabled = false;

  private readonly internals = this.attachInternals();
  private readonly fieldId = `ws-text-field-${++nextTextFieldId}`;
  private readonly helperId = `${this.fieldId}-helper`;
  private readonly errorId = `${this.fieldId}-error`;
  private defaultValue = '';
  private capturedDefaultValue = false;
  private customValidationMessage = '';

  private readonly contentObserver = new MutationObserver(() => {
    this.syncSlottedState();
  });

  override connectedCallback() {
    super.connectedCallback();
    this.contentObserver.observe(this, {
      attributeFilter: ['slot'],
      attributes: true,
      childList: true,
      subtree: true,
    });

    if (!this.capturedDefaultValue) {
      this.defaultValue = this.getAttribute('value') ?? this.value;
      this.capturedDefaultValue = true;
    }

    this.syncSlottedState();
  }

  override disconnectedCallback() {
    this.contentObserver.disconnect();
    super.disconnectedCallback();
  }

  protected override firstUpdated() {
    this.syncFormAndValidity();
  }

  protected override updated() {
    this.syncFormAndValidity();
  }

  override render() {
    const isDisabled = this.isEffectivelyDisabled;
    const isInvalid = this.isVisuallyInvalid;
    const supportingText = isInvalid
      ? this.errorText ||
        this.customValidationMessage ||
        this.validationMessage ||
        'Invalid value.'
      : this.helperText;
    const supportingId = supportingText
      ? isInvalid
        ? this.errorId
        : this.helperId
      : undefined;
    const canClear =
      this.clearable && Boolean(this.value) && !this.readOnly && !isDisabled;

    return html`
      ${this.label
        ? html`<label class="label" part="label" for=${this.fieldId}
            >${this.label}${this.required
              ? html`<span class="required" aria-hidden="true"> *</span>`
              : nothing}</label
          >`
        : nothing}
      <div
        class="control ${isInvalid ? 'invalid' : ''} ${isDisabled
          ? 'disabled'
          : ''}"
        data-shape=${this.effectiveShape}
        part="control"
      >
        ${this.hasLeadingIcon
          ? html`<span class="icon leading-icon" aria-hidden="true"
              ><slot name="leading-icon" @slotchange=${this.syncSlottedState}></slot
            ></span>`
          : html`<slot
              name="leading-icon"
              class="hidden-slot"
              @slotchange=${this.syncSlottedState}
            ></slot>`}
        ${this.renderNativeControl(isDisabled, isInvalid, supportingId)}
        ${this.hasTrailingIcon
          ? html`<span class="icon trailing-icon" aria-hidden="true"
              ><slot
                name="trailing-icon"
                @slotchange=${this.syncSlottedState}
              ></slot
            ></span>`
          : html`<slot
              name="trailing-icon"
              class="hidden-slot"
              @slotchange=${this.syncSlottedState}
            ></slot>`}
        ${canClear ? this.renderClearButton() : nothing}
      </div>
      ${supportingText
        ? html`<div
            id=${supportingId!}
            class="supporting-text ${isInvalid ? 'error' : ''}"
            part="supporting-text"
            aria-live=${isInvalid ? 'polite' : 'off'}
          >
            ${supportingText}
          </div>`
        : nothing}
    `;
  }

  /** Associated form, when the field is inside one. */
  get form(): HTMLFormElement | null {
    return this.internals.form;
  }

  /** Labels associated with the custom element. */
  get labels(): NodeList {
    return this.internals.labels;
  }

  /** Current validity state. */
  get validity(): ValidityState {
    return this.internals.validity;
  }

  /** Current validation message. */
  get validationMessage(): string {
    return this.internals.validationMessage;
  }

  /** Whether the field participates in constraint validation. */
  get willValidate(): boolean {
    return this.internals.willValidate;
  }

  override focus(options?: FocusOptions) {
    this.inputElement?.focus(options);
  }

  /** Selects the complete field value. */
  select() {
    this.inputElement?.select();
  }

  /** Applies a custom validity message. Pass an empty string to clear it. */
  setCustomValidity(message: string) {
    this.customValidationMessage = message;
    this.syncFormAndValidity();
  }

  /** Runs constraint validation without displaying browser UI. */
  checkValidity(): boolean {
    this.syncFormAndValidity();
    return this.internals.checkValidity();
  }

  /** Runs constraint validation and makes the invalid state visible. */
  reportValidity(): boolean {
    this.touched = true;
    this.syncFormAndValidity();
    return this.internals.reportValidity();
  }

  formDisabledCallback(disabled: boolean) {
    this.formDisabled = disabled;
  }

  formResetCallback() {
    this.value = this.defaultValue;
    this.touched = false;
  }

  formStateRestoreCallback(state: string | File | FormData | null) {
    if (typeof state === 'string') {
      this.value = state;
    }
  }

  private get inputElement(): WsTextFieldControl | null {
    return this.shadowRoot?.querySelector<WsTextFieldControl>('.input') ?? null;
  }

  private get effectiveShape(): WsTextFieldShape {
    return this.shape ?? (this.type === 'search' ? 'circle' : 'default');
  }

  private get isEffectivelyDisabled(): boolean {
    return this.disabled || this.formDisabled;
  }

  private get isVisuallyInvalid(): boolean {
    return this.invalid || (this.touched && !this.validity.valid);
  }

  private renderNativeControl(
    isDisabled: boolean,
    isInvalid: boolean,
    supportingId: string | undefined
  ) {
    const sharedAria = {
      label: this.label ? undefined : this.accessibleLabel,
      describedBy: supportingId,
      errorMessage: isInvalid ? this.errorId : undefined,
    };

    if (this.type === 'textarea') {
      return html`
        <textarea
          id=${this.fieldId}
          class="input textarea"
          part="input"
          .value=${this.value}
          rows=${this.rows}
          placeholder=${this.placeholder}
          .autocomplete=${this.autocomplete}
          inputmode=${ifDefined(this.inputMode || undefined)}
          minlength=${ifDefined(this.minLength)}
          maxlength=${ifDefined(this.maxLength)}
          ?required=${this.required}
          ?disabled=${isDisabled}
          ?readonly=${this.readOnly}
          aria-label=${ifDefined(sharedAria.label)}
          aria-describedby=${ifDefined(sharedAria.describedBy)}
          aria-errormessage=${ifDefined(sharedAria.errorMessage)}
          aria-invalid=${isInvalid ? 'true' : 'false'}
          @input=${this.handleInput}
          @change=${this.handleChange}
          @blur=${this.handleBlur}
          @invalid=${this.handleInvalid}
        ></textarea>
      `;
    }

    return html`
      <input
        id=${this.fieldId}
        class="input"
        part="input"
        .value=${this.value}
        type=${this.type}
        placeholder=${this.placeholder}
        .autocomplete=${this.autocomplete}
        inputmode=${ifDefined(this.inputMode || undefined)}
        minlength=${ifDefined(this.minLength)}
        maxlength=${ifDefined(this.maxLength)}
        min=${ifDefined(this.min)}
        max=${ifDefined(this.max)}
        .step=${this.step ?? ''}
        pattern=${ifDefined(this.pattern)}
        ?required=${this.required}
        ?disabled=${isDisabled}
        ?readonly=${this.readOnly}
        aria-label=${ifDefined(sharedAria.label)}
        aria-describedby=${ifDefined(sharedAria.describedBy)}
        aria-errormessage=${ifDefined(sharedAria.errorMessage)}
        aria-invalid=${isInvalid ? 'true' : 'false'}
        @input=${this.handleInput}
        @change=${this.handleChange}
        @blur=${this.handleBlur}
        @invalid=${this.handleInvalid}
      />
    `;
  }

  private renderClearButton() {
    const label = this.label ? `Clear ${this.label}` : 'Clear field';
    return html`
      <button
        class="clear-button"
        part="clear-button"
        type="button"
        aria-label=${label}
        @click=${this.clearValue}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7.4 6 12 10.6 16.6 6 18 7.4 13.4 12l4.6 4.6-1.4 1.4-4.6-4.6L7.4 18 6 16.6l4.6-4.6L6 7.4 7.4 6Z"></path>
        </svg>
      </button>
    `;
  }

  private handleInput(event: InputEvent) {
    event.stopPropagation();
    const input = event.currentTarget as WsTextFieldControl;
    this.value = input.value;
    this.syncFormAndValidity();
    this.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        composed: true,
        data: event.data,
        inputType: event.inputType,
        isComposing: event.isComposing,
      })
    );
  }

  private handleChange(event: Event) {
    event.stopPropagation();
    this.touched = true;
    this.syncFormAndValidity();
    this.dispatchEvent(new Event('change', {bubbles: true, composed: true}));
  }

  private handleBlur() {
    this.touched = true;
    this.syncFormAndValidity();
  }

  private handleInvalid() {
    this.touched = true;
  }

  private clearValue() {
    this.value = '';
    this.touched = true;
    this.syncFormAndValidity();
    this.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        composed: true,
        inputType: 'deleteContent',
      })
    );
    this.dispatchEvent(new Event('change', {bubbles: true, composed: true}));
    this.updateComplete.then(() => this.focus());
  }

  private syncSlottedState = () => {
    const hasLeadingIcon = this.querySelector('[slot="leading-icon"]') !== null;
    const hasTrailingIcon = this.querySelector('[slot="trailing-icon"]') !== null;

    if (this.hasLeadingIcon !== hasLeadingIcon) {
      this.hasLeadingIcon = hasLeadingIcon;
    }

    if (this.hasTrailingIcon !== hasTrailingIcon) {
      this.hasTrailingIcon = hasTrailingIcon;
    }
  };

  private syncFormAndValidity() {
    const input = this.inputElement;
    this.internals.setFormValue(this.isEffectivelyDisabled ? null : this.value);

    if (!input) return;

    this.syncNativeInput(input);
    input.setCustomValidity(this.customValidationMessage);

    if (this.invalid) {
      this.internals.setValidity(
        {customError: true},
        this.errorText || this.customValidationMessage || 'Invalid value.',
        input
      );
      return;
    }

    const flags = this.toValidityFlags(input.validity);
    this.internals.setValidity(flags, input.validationMessage, input);
  }

  private syncNativeInput(input: WsTextFieldControl) {
    input.value = this.value;
    input.placeholder = this.placeholder;
    input.required = this.required;
    input.disabled = this.isEffectivelyDisabled;
    input.readOnly = this.readOnly;
    input.autocomplete = this.autocomplete;
    input.inputMode = this.inputMode;

    this.syncOptionalInputAttribute(input, 'minlength', this.minLength);
    this.syncOptionalInputAttribute(input, 'maxlength', this.maxLength);

    if (input instanceof HTMLTextAreaElement) {
      input.rows = this.rows;
      return;
    }

    input.type = this.type;
    this.syncOptionalInputAttribute(input, 'min', this.min);
    this.syncOptionalInputAttribute(input, 'max', this.max);
    this.syncOptionalInputAttribute(input, 'step', this.step);
    this.syncOptionalInputAttribute(input, 'pattern', this.pattern);
  }

  private syncOptionalInputAttribute(
    input: WsTextFieldControl,
    name: string,
    value: string | number | undefined
  ) {
    if (value === undefined) {
      input.removeAttribute(name);
      return;
    }

    input.setAttribute(name, String(value));
  }

  private toValidityFlags(validity: ValidityState): ValidityStateFlags {
    const flags: ValidityStateFlags = {};
    if (validity.badInput) flags.badInput = true;
    if (validity.customError) flags.customError = true;
    if (validity.patternMismatch) flags.patternMismatch = true;
    if (validity.rangeOverflow) flags.rangeOverflow = true;
    if (validity.rangeUnderflow) flags.rangeUnderflow = true;
    if (validity.stepMismatch) flags.stepMismatch = true;
    if (validity.tooLong) flags.tooLong = true;
    if (validity.tooShort) flags.tooShort = true;
    if (validity.typeMismatch) flags.typeMismatch = true;
    if (validity.valueMissing) flags.valueMissing = true;
    return flags;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ws-text-field': WsTextField;
  }
}